import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, ImagePlus, ShieldCheck, X, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const StarRow = ({ value, size = 4, onChange }) => (
  <div className="flex items-center gap-0.5" data-testid="star-row">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n} type="button"
        onClick={() => onChange && onChange(n)}
        className={`${onChange ? "cursor-pointer" : "cursor-default"}`}
        data-testid={onChange ? `star-${n}` : undefined}
      >
        <Star className={`h-${size} w-${size} ${n <= value ? "text-primary fill-current" : "text-muted-foreground/40"}`} />
      </button>
    ))}
  </div>
);

export default function ReviewsSection({ product }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: reviews = [] } = useQuery({
    queryKey: ["reviews", product.product_id],
    queryFn: () => api.get(`/reviews/${product.product_id}`).then((r) => r.data),
  });
  const [open, setOpen] = useState(false);
  const avg = reviews.length ? reviews.reduce((a, r) => a + r.rating, 0) / reviews.length : (product.rating || 0);

  return (
    <section className="mt-16" data-testid="reviews-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Customer reviews</h2>
          <div className="flex items-center gap-2 mt-1">
            <StarRow value={Math.round(avg)} size={4} />
            <span className="text-sm text-muted-foreground">{avg.toFixed(1)} · {reviews.length} reviews</span>
          </div>
        </div>
        <Button className="btn-primary" onClick={() => user ? setOpen(true) : toast.error("Sign in to write a review")} data-testid="write-review-btn">Write a review</Button>
      </div>

      {reviews.length === 0 ? (
        <p className="text-muted-foreground text-sm">No reviews yet. Be the first to review this product.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {reviews.map((r) => (
            <div key={r.review_id} className="rounded-2xl bg-card border border-white/5 p-5" data-testid={`review-${r.review_id}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                  {r.user_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{r.user_name}</p>
                    {r.verified && <span className="chip text-primary border-primary/30"><ShieldCheck className="h-3 w-3" /> Verified buyer</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRow value={r.rating} />
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              {r.title && <p className="font-semibold mt-3">{r.title}</p>}
              {r.body && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{r.body}</p>}
              {r.photos?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.photos.map((p, i) => (
                    <img key={p || i} src={`${API}/files/${p}`} className="h-20 w-20 rounded-lg object-cover border border-white/10" alt="" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {open && (
        <WriteReview
          product={product}
          onClose={() => setOpen(false)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["reviews", product.product_id] }); qc.invalidateQueries({ queryKey: ["product", product.slug] }); setOpen(false); }}
        />
      )}
    </section>
  );
}

function WriteReview({ product, onClose, onSaved }) {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [photos, setPhotos] = useState([]); // storage paths
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const upload = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads/review-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.path);
      }
      setPhotos((prev) => [...prev, ...uploaded].slice(0, 4));
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/reviews", { product_id: product.product_id, rating, title, body, photos });
      toast.success("Review posted");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post review"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="write-review-dialog">
        <DialogHeader><DialogTitle>Write a review</DialogTitle><DialogDescription className="text-muted-foreground text-sm">Share your experience with this product. Photos optional.</DialogDescription></DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Your rating</p>
            <StarRow value={rating} size={6} onChange={setRating} />
          </div>
          <div><Input placeholder="Sum it up in a line" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="review-title-input" /></div>
          <div><Textarea rows={4} placeholder="Tell others what you thought…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="review-body-input" /></div>
          <div>
            <p className="text-sm text-muted-foreground mb-2">Photos (optional, up to 4)</p>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={p || i} className="relative">
                  <img src={`${API}/files/${p}`} className="h-16 w-16 rounded-lg object-cover" alt="" />
                  <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-black/80 flex items-center justify-center"><X className="h-3 w-3" /></button>
                </div>
              ))}
              {photos.length < 4 && (
                <button onClick={() => fileRef.current?.click()} className="h-16 w-16 rounded-lg border border-dashed border-white/15 flex items-center justify-center hover:border-primary" data-testid="review-add-photo">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <ImagePlus className="h-5 w-5 text-muted-foreground" />}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files && upload(Array.from(e.target.files))} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="border-white/15" onClick={onClose}>Cancel</Button>
            <Button className="btn-primary" onClick={save} disabled={saving || uploading} data-testid="review-submit">{saving ? "Posting…" : "Post review"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
